import React from 'react';
import { Account } from '../../interfaces/accounting.interface';
import SearchableSelect from './SearchableSelect';

export const accountLabel = (a: Account): string => `${a.code} - ${a.name}`;

interface AccountAutocompleteProps {
  /** Accounts to choose from (caller may pass a pre-filtered subset, e.g. by type). */
  accounts: Account[];
  /** Selected account id ('' = none). */
  value: string;
  /** Called with the chosen account id, or '' when cleared. */
  onChange: (accountId: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  /** Placeholder shown when empty — e.g. "use settings default". */
  placeholder?: string;
  'data-testid'?: string;
}

/**
 * Searchable Chart-of-Accounts picker: users can type part of an account code
 * or name to filter, so long charts stay usable and no leaf account is out of
 * reach.
 *
 * A thin wrapper over `SearchableSelect` — dropdown behaviour lives there, this
 * only fixes the option type and the `code - name` label.
 *
 * The value in/out is the account id, so existing callers that store an id need
 * no change.
 */
export const AccountAutocomplete: React.FC<AccountAutocompleteProps> = ({
  accounts,
  value,
  onChange,
  ...rest
}) => (
  <SearchableSelect<Account>
    options={accounts}
    value={value}
    onChange={accountId => onChange(accountId)}
    getOptionLabel={accountLabel}
    noOptionsText="No accounts found"
    {...rest}
  />
);

export default AccountAutocomplete;
